import { useFlags } from 'launchdarkly-react-client-sdk'
import { useCallback } from 'react'
import { useDispatch, useStore } from 'react-redux'
import { generatePath, useHistory } from 'react-router-dom'

import { PATHS } from '@/config/router/paths'
import type { AppDispatch } from '@/config/store/store'
import {
  consumeStashedPostCreateCampaignsBaseline,
  refreshCampaignsCache
} from '@/ducks/campaigns/campaigns.cache.helpers'
import type { RootState } from '@/ducks/root.reducer'

export type OffersFormBackTab =
  | 'item_discount'
  | 'cart_discount'
  | 'buyX_getY'

export type OffersFormMode = 'create' | 'edit'

/**
 * After a successful create/edit submit, refresh campaigns cache and return
 * to the marketing campaigns page.
 */
export function useOfferFormSuccessNavigate(_tab: OffersFormBackTab) {
  const history = useHistory()
  const dispatch = useDispatch()
  const store = useStore()

  return useCallback(
    async (mode: OffersFormMode) => {
      let campaignsBaseline = consumeStashedPostCreateCampaignsBaseline()

      if (mode === 'create') {
        await refreshCampaignsCache(
          dispatch as AppDispatch,
          store.getState as () => RootState
        )
      }

      history.replace(PATHS.MARKETING_CAMPAIGNS, {
        resetCampaignsPagination: mode === 'create',
        campaignsBaselineTotal: campaignsBaseline?.total,
        campaignsBaselineIds: campaignsBaseline?.campaignIds
      })
    },
    [dispatch, history, store]
  )
}

/**
 * Navigate back from create/edit offer flows: Marketing tools page when LD flag is on,
 * otherwise the Offers tab that matches `tab`.
 */
export function useOfferFormNavigateBack(tab: OffersFormBackTab) {
  const history = useHistory()
  const { lmd_store_web_offers_marketing } = useFlags()

  return useCallback(() => {
    if (lmd_store_web_offers_marketing) {
      history.push(PATHS.MARKETING_TOOLS)
      return
    }
    history.push(generatePath(PATHS.OFFERS_BY_ID, { id: tab }))
  }, [history, lmd_store_web_offers_marketing, tab])
}
